import { KalDate } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { SharedListingDatasource } from 'src/app/_shared/components/listing/shared-listing.datasource';
import { KalTableDatasourceParameters } from 'src/app/_shared/modules/kal-table/classes/kal-table-datasource';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { KalTablePageEvent } from 'src/app/_shared/modules/kal-table/classes/managers/kal-table-page-manager';
import { ColumnFilterType } from 'src/app/_shared/modules/kal-table/kal-table-header/kal-table-header.component';
import { KalPaginatorComponent } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { UserState } from 'src/app/_user/store/user/user.state';
import { parseCSVToArray } from 'src/app/utils/array.utils';
import { isEmpty } from 'src/app/utils/object.utils';
import { environment } from 'src/environments/environment';
import {
    ChargerVueModeExpertBenefModelDto,
    CriteresBenefDtoInput,
    InfoColDataGridModelDto,
    InitialiserModeExpertBenefModelDto,
    MutationGetInitialisationModeExpertModelArgs,
    MutationGetVueModeExpertModelArgs,
    TypesValeursEnum,
    VueDto
} from 'src/graphql/generated-types';

export class SharedBeneficiaryListDatasource extends SharedListingDatasource<ChargerVueModeExpertBenefModelDto> {
    CurrentVue: VueDto;
    OrdreDeTri: string[] = [];
    BenefAvecDroitActif = true;
    NombreBenefTot: number;
    NombreBenefTotFiltre: number;
    ListeVues: VueDto[] = [];
    columnsDto: InfoColDataGridModelDto[];
    dataLoading = new BehaviorSubject(false);
    private lastPage: KalTablePageEvent = {} as KalTablePageEvent;
    private lastFilters: any = {};
    private hiddenBeneficiaryIdList: number[] = [];

    constructor(datasourceParameters: KalTableDatasourceParameters,
                private readonly graphqlService: GraphqlService,
                protected readonly store: Store) {
        super(datasourceParameters);

        this.hiddenBeneficiaryIdList = datasourceParameters.filters.hiddenBeneficiaryIdList;
    }

    get columns(): KalTableHeader[] {
        return (this.columnsDto || []).map(c => ({
            label: c.NomColonne,
            code: c.CodeColonne,
            sortable: true,
            customComparator:  (sort) => (a: any, b: any) => {
                const dir = sort.direction === 'asc' ? 1 : -1;
                const type = c.TypeValeur;

                // Formate la valeur en fonction du type
                const valueByType = (value: any): any => {
                    if (type === TypesValeursEnum.Texte) {
                        return (value || '').toLocaleLowerCase();
                    }
                    if (type === TypesValeursEnum.Date) {
                        return new KalDate(value, environment.date.format.human).toDate();
                    }
                    if (type === TypesValeursEnum.Decimal) {
                        return !!value ? +value.replace(',', '.') : null;
                    }
                    return (value || '');
                };

                const val1 = valueByType(a[sort.code]);
                const val2 = valueByType(b[sort.code])

                if(val1 === null) {
                    return dir * -1;
                }
                if(val2 === null) {
                    return dir * 1;
                }
                return val1 > val2 ? dir * 1 : dir * -1;
            },
            columnFilterType: this.buildColumnFilterType(c),
            columnFilterList: this.buildColumnFilterType(c) === ColumnFilterType.List
                ? (c.ListeValeurs?.KeyValueModelDTO || []).map(keyValueModel => ({
                    code: ''+keyValueModel.Id,
                    label: keyValueModel.Valeur,
                    isDisplayed: true
                }))
                : null
        }));
    }

    get isDelegationGlobal(): boolean {
        return this.store.selectSnapshot(UserState.currentDelegation).CEIdcGlob;
    }

    private get criteria(): CriteresBenefDtoInput {
        const {BenefAvecDroitActif, OrdreDeTri} = this;
        return {
            BenefAvecDroitActif,
            IsOuvrantDroit: true,
            // Dépendant du fait que la délégation connectée soit la délégation globale
            OnDelegationGlobale: this.isDelegationGlobal,
            BenefSansBlocageDroit: null, // critère toujours null côté back
            // Tri des éléments
            OrdreDeTri: {
                string: OrdreDeTri
            },
        };
    }

    /**
     * Fonction utilisée seulement dans le cadre des modales de filtrage bénef.
     * Si on est en sélection multiple il est possible de sélectionner tous les bénéficiaires donc on fait une requête
     * qui va s'en charger.
     */
    getAll$(): Observable<{ [key: string]: string }[]> {
        return this.getVueModeExpertModelV2$({number: 0, size: KalPaginatorComponent.defaultRowsByPageList[4]})
            .pipe(
                map(GetVueModeExpertModel => (this.transformCSV(GetVueModeExpertModel.Requete) || [])),
                catchError(err => throwError(err))
            );
    }

    countFilters(): number {
        // some filters values aren't meant to be counted as applied filter
        const excludedFiltersList = [
            'PrendreOrgaAssocie',
            'PrendreSiteAssocie',
            'VoirBenefEnErreur',
            'VoirBenefSansProfilKalidea',
            'VoirBenefSubNonSynchro',
            'VoirBenefNonSynchro',
            'DateNaissanceOperator'
        ];
        const filters = this.filters.value || {};
        if ([filters['DateNaissanceStart'], filters['DateNaissanceEnd']].every(e => !!e)) {
            excludedFiltersList.push('DateNaissanceEnd');
        }

        const isExcludedFromFiltersCount = (value, key): boolean => {
            return isEmpty(value)
                || excludedFiltersList.includes(key);
        };

        return super.countFilters(isExcludedFromFiltersCount);
    }

    fetchRows(config: KalTableDatasourceParameters = {}, options: { forceRefresh?: boolean } = {forceRefresh: false}): Observable<any> {
        const {sort, filters, page} = config;
        const comparator = this.getComparator(sort);

        // loader par défaut
        let loadData$: Observable<any> = of(null);

        // si on a un comparator
        // et que les filtres n'ont pas changés
        // et qu'il y a déjà des données dans le tableau,
        // => on va réaliser un tri côté front sans appel à l'api
        if (comparator
            && isEqual(this.lastPage, page)
            && isEqual(filters, this.lastFilters)
            && this.rows.length > 0
            && !options.forceRefresh) {
            this.dataLoading.next(true);
            this.setRows(this.rows.sort(comparator));
            this.dataLoading.next(false);
        } else {
            this.dataLoading.next(true);

            if (!this.CurrentVue) {
                loadData$ = this.initialize$().pipe(
                    switchMap(() => this.getVueModeExpertModel$(page))
                );
            } else {
                loadData$ = this.getVueModeExpertModel$(page);
            }

        }

        return loadData$.pipe(
            tap(() => {
                this.lastFilters = filters;
                this.lastPage = page;
                this.dataLoading.next(false);
            }),
            catchError(err => {
                this.dataLoading.next(false);
                return throwError(() => err);
            })
        );
    }

    private buildMutationVariables(page: KalTablePageEvent): MutationGetVueModeExpertModelArgs {
        const { size,number } = page || { size: KalPaginatorComponent.defaultPageSize, number: 0 };
        return {
            idVue: this.CurrentVue?.Id,
            criteres: {
                ...this.criteria,
                // Nombre de bénéficiaire à afficher
                NbLignes: size,
                // Pagination, nombre d'élément par page * page actuelle
                LigneDepart: number * size
            },
            filtre: {
                VoirDroitActif: !!this.BenefAvecDroitActif,
                VoirHorsDroit: this.BenefAvecDroitActif === false,
                VoirTousDroits: this.BenefAvecDroitActif === null,
                ...this.filters.value
            },
        };
    }

    /**
     * transforme le csv reçu de l'API ( data|data|data)
     * en tableau utilisable par le kal-table ( [{ID: data, NOM: data, PRENOM: data}]
     */
    private transformCSV(csv: string): { [key: string]: string }[] {
        return parseCSVToArray(csv)
            .map(l => {
                const lignes: any = {};
                this.columns.forEach((value, index) => {
                    lignes[value.code] = l[index];
                });

                return lignes;
            }); // transform array like to object
    }

    /**
     * Récupère les vues dispo pour la liste des bénéficiaires
     */
    private initialize$(): Observable<InitialiserModeExpertBenefModelDto> {
        return this.graphqlService.mutateOne<'GetInitialisationModeExpertModel', MutationGetInitialisationModeExpertModelArgs>({
            mutation: gql`
                mutation BenefListeInitialisation(
                    $contexte: ContextInfosInput
                    $criteres: CriteresBenefDTOInput
                ) {
                    GetInitialisationModeExpertModel(contexte: $contexte, criteres: $criteres){
                        ListeVues {
                            VueDTO {
                                Code
                                Libelle
                                Id
                            }
                        }
                        VueParDefaut {
                            Id
                        }
                        NombreBenefTot
                    }
                }
            `,
            variables: {
                criteres: {
                    IsOuvrantDroit: true,
                    BenefAvecDroitActif: true,
                    BenefSansBlocageDroit: null,
                    // Dépendant du fait que la délégation connectée soit la délégation globale
                    OnDelegationGlobale: this.isDelegationGlobal,
                } as CriteresBenefDtoInput
            }
        }).pipe(
            tap(GetInitialisationModeExpertModel => {
                // store values
                this.ListeVues = GetInitialisationModeExpertModel?.ListeVues?.VueDTO || [];

                // malheureusement la vue par défaut ne contient que son ID,
                // il faut donc rechercher dans toutes les vues disponibles laquelle possède cet Id
                if (!this.CurrentVue) {
                    this.CurrentVue = this.ListeVues.find(l => !GetInitialisationModeExpertModel.VueParDefaut
                            || l.Id === GetInitialisationModeExpertModel.VueParDefaut.Id);
                }
                this.NombreBenefTot = GetInitialisationModeExpertModel.NombreBenefTot;
            })
        );
    }

    private getVueModeExpertModelV2$(page: KalTablePageEvent): Observable<ChargerVueModeExpertBenefModelDto> {
        return this.graphqlService.mutateOne<'GetVueModeExpertModel', MutationGetVueModeExpertModelArgs>({
            mutation: gql`
                mutation GetVueModeExpertModelV2($contexte: ContextInfosInput, $filtre: FiltrageBenefModeExpertModelDTOInput, $criteres: CriteresBenefDTOInput, $idVue: Float) {
                    GetVueModeExpertModelV2(contexte: $contexte, criteres: $criteres, filtre: $filtre, idVue: $idVue) {
                        ListeInfoCol {
                            InfoColDataGridModelDTO {
                                CodeColonne
                                IdFicheProfil
                                IsReadOnly
                                LargeurColonne
                                ListeValeurs {
                                    KeyValueModelDTO {
                                        Id
                                        Valeur
                                    }
                                }
                                MaxLength
                                NomColonne
                                OrdreColonne
                                Status
                                TypeValeur
                                ValeurMax
                                ValeurMin
                            }
                        }
                        NombreBenefTot
                        NombreBenefTotFiltre
                        Requete
                    }
                }
            `,
            variables: this.buildMutationVariables(page)
        });
    }

    private getVueModeExpertModel$(page: KalTablePageEvent): Observable<ChargerVueModeExpertBenefModelDto> {
        return this.getVueModeExpertModelV2$(page)
            .pipe(
                tap(data => {
                    this.nbOfElementsValue = data.NombreBenefTotFiltre;
                    this.columnsDto = data.ListeInfoCol.InfoColDataGridModelDTO;
                    this.NombreBenefTot = data.NombreBenefTot;
                    this.NombreBenefTotFiltre = data.NombreBenefTotFiltre;
                    const rows = this.transformCSV(data.Requete);
                    const filteredRows = rows.filter(row => !this.hiddenBeneficiaryIdList?.includes(+row.ID));

                    this.setRows(filteredRows);
                })
            );
    }

    private buildColumnFilterType(col: InfoColDataGridModelDto): ColumnFilterType {
        switch (col.TypeValeur) {
            case TypesValeursEnum.Texte:
                return ColumnFilterType.Text;
            case TypesValeursEnum.Date:
                return ColumnFilterType.Date;
            case TypesValeursEnum.Decimal:
            case TypesValeursEnum.Entier:
                return ColumnFilterType.Number;
            case TypesValeursEnum.ListeValeurs:
                return ColumnFilterType.List;
            case TypesValeursEnum.Bool:
                return null;
            default:
                return ColumnFilterType.Text;
        }
    }
}
